import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }

    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        const transparentHeaderWithSibling =
          header instanceof HTMLElement &&
          header.hasAttribute('transparent') &&
          header.parentElement?.nextElementSibling;

        if (entry.target !== header || transparentHeaderWithSibling) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }

        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;

          for (let i = 0; i < children.length; i++) {
            const element = children[i];

            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

/* =========================================================
   Flying Shark Dropdown Delay + Width Fix
   - Hover link opens dropdown
   - Dropdown closes after 5 seconds
   - Dropdown text never breaks like Jea / ns
   - Chevron down/up added by JS
   ========================================================= */

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');

  if (!(header instanceof HTMLElement)) return;

  if (header.flyingSharkDropdownDelayController) {
    header.flyingSharkDropdownDelayController.abort();
  }

  const controller = new AbortController();
  header.flyingSharkDropdownDelayController = controller;
  const { signal } = controller;

  const CLOSE_DELAY = 5000;

  let activeItem = null;
  let closeTimer = null;

  const dropdownItems = Array.from(
    header.querySelectorAll('.header-menu .menu-list__list > .menu-list__list-item')
  ).filter((item) => {
    if (!(item instanceof HTMLElement)) return false;
    return item.querySelector('.menu-list__submenu, .mega-menu__list, details');
  });

  function setImportantStyle(element, property, value) {
    if (!(element instanceof HTMLElement)) return;
    element.style.setProperty(property, value, 'important');
  }

  function removeStyle(element, property) {
    if (!(element instanceof HTMLElement)) return;
    element.style.removeProperty(property);
  }

  function getTrigger(item) {
    return item.querySelector(
      ':scope > .menu-list__link, :scope > a, :scope > summary, :scope > details > summary'
    );
  }

  function getDetails(item) {
    if (item.matches('details')) return item;
    return item.querySelector(':scope > details');
  }

  function getSubmenus(item) {
    return Array.from(
      item.querySelectorAll(
        ':scope > .menu-list__submenu, :scope > .mega-menu__list, :scope > details > .menu-list__submenu, :scope > details > .mega-menu__list'
      )
    );
  }

  function getFirstSubmenu(item) {
    return getSubmenus(item)[0] || null;
  }

  function styleTopTrigger(trigger) {
    if (!(trigger instanceof HTMLElement)) return;

    setImportantStyle(trigger, 'display', 'inline-flex');
    setImportantStyle(trigger, 'align-items', 'center');
    setImportantStyle(trigger, 'white-space', 'nowrap');
    setImportantStyle(trigger, 'word-break', 'normal');
    setImportantStyle(trigger, 'overflow-wrap', 'normal');
    setImportantStyle(trigger, 'gap', '0px');

    trigger.querySelectorAll('.svg-wrapper.icon-caret, .icon-caret').forEach((caret) => {
      if (caret instanceof HTMLElement) {
        setImportantStyle(caret, 'display', 'none');
      }
    });
  }

  function setupChevron(item) {
    const trigger = getTrigger(item);

    if (!(trigger instanceof HTMLElement)) return;

    styleTopTrigger(trigger);

    let chevron = trigger.querySelector('.fs-delay-chevron');

    if (!(chevron instanceof HTMLElement)) {
      chevron = document.createElement('span');
      chevron.className = 'fs-delay-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      trigger.appendChild(chevron);
    }

    chevron.textContent = item === activeItem ? '⌃' : '⌄';

    setImportantStyle(chevron, 'display', 'inline-flex');
    setImportantStyle(chevron, 'align-items', 'center');
    setImportantStyle(chevron, 'justify-content', 'center');
    setImportantStyle(chevron, 'font-size', '11px');
    setImportantStyle(chevron, 'line-height', '1');
    setImportantStyle(chevron, 'margin-left', '3px');
    setImportantStyle(chevron, 'position', 'relative');
    setImportantStyle(chevron, 'top', '-1px');
    setImportantStyle(chevron, 'pointer-events', 'none');
  }

  function setChevron(item, isOpen) {
    const trigger = getTrigger(item);
    const chevron = trigger instanceof HTMLElement ? trigger.querySelector('.fs-delay-chevron') : null;

    if (chevron instanceof HTMLElement) {
      chevron.textContent = isOpen ? '⌃' : '⌄';
    }
  }

  function styleDropdownLinks(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    const links = submenu.querySelectorAll('a, .menu-list__link');

    links.forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      setImportantStyle(link, 'display', 'flex');
      setImportantStyle(link, 'align-items', 'center');
      setImportantStyle(link, 'justify-content', 'flex-start');
      setImportantStyle(link, 'width', '100%');
      setImportantStyle(link, 'min-width', '220px');
      setImportantStyle(link, 'max-width', 'none');
      setImportantStyle(link, 'white-space', 'nowrap');
      setImportantStyle(link, 'word-break', 'normal');
      setImportantStyle(link, 'overflow-wrap', 'normal');
      setImportantStyle(link, 'line-height', '1.35');
      setImportantStyle(link, 'text-align', 'left');
    });
  }

  function showSubmenuStyles(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    submenu.removeAttribute('hidden');
    submenu.setAttribute('data-active', 'true');

    setImportantStyle(submenu, 'display', 'block');
    setImportantStyle(submenu, 'opacity', '1');
    setImportantStyle(submenu, 'visibility', 'visible');
    setImportantStyle(submenu, 'pointer-events', 'auto');
    setImportantStyle(submenu, 'content-visibility', 'visible');

    setImportantStyle(submenu, 'min-width', '240px');
    setImportantStyle(submenu, 'width', 'max-content');
    setImportantStyle(submenu, 'max-width', 'calc(100vw - 40px)');
    setImportantStyle(submenu, 'white-space', 'normal');
    setImportantStyle(submenu, 'overflow', 'visible');
    setImportantStyle(submenu, 'z-index', '9999');

    styleDropdownLinks(submenu);
  }

  function hideSubmenuStyles(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    submenu.removeAttribute('data-active');

    removeStyle(submenu, 'display');
    removeStyle(submenu, 'opacity');
    removeStyle(submenu, 'visibility');
    removeStyle(submenu, 'pointer-events');
    removeStyle(submenu, 'content-visibility');

    removeStyle(submenu, 'min-width');
    removeStyle(submenu, 'width');
    removeStyle(submenu, 'max-width');
    removeStyle(submenu, 'white-space');
    removeStyle(submenu, 'overflow');
    removeStyle(submenu, 'z-index');
  }

  function updateHeaderOpenHeight(item) {
    const submenu = getFirstSubmenu(item);

    if (!(submenu instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const topRow = header.querySelector('.header__row--top');
      const topRowHeight = topRow instanceof HTMLElement ? topRow.getBoundingClientRect().height : 60;
      const submenuHeight = submenu.scrollHeight || submenu.getBoundingClientRect().height || 0;

      header.style.setProperty('--full-open-header-height', `${Math.ceil(topRowHeight + submenuHeight)}px`);
    });
  }

  function openDropdown(item) {
    if (!(item instanceof HTMLElement)) return;

    clearTimeout(closeTimer);

    if (activeItem && activeItem !== item) {
      closeDropdown(activeItem);
    }

    activeItem = item;
    item.setAttribute('data-fs-dropdown-open', 'true');

    const trigger = getTrigger(item);
    const details = getDetails(item);

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'true');
      styleTopTrigger(trigger);
    }

    if (details instanceof HTMLElement) {
      details.setAttribute('open', '');
    }

    getSubmenus(item).forEach((submenu) => {
      showSubmenuStyles(submenu);
    });

    setChevron(item, true);
    updateHeaderOpenHeight(item);
  }

  function closeDropdown(item) {
    if (!(item instanceof HTMLElement)) return;

    item.removeAttribute('data-fs-dropdown-open');

    const trigger = getTrigger(item);
    const details = getDetails(item);

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (details instanceof HTMLElement) {
      details.removeAttribute('open');
    }

    getSubmenus(item).forEach((submenu) => {
      hideSubmenuStyles(submenu);
    });

    setChevron(item, false);

    if (activeItem === item) {
      activeItem = null;
      header.style.setProperty('--full-open-header-height', '0px');
    }
  }

  function scheduleClose() {
    clearTimeout(closeTimer);

    closeTimer = setTimeout(() => {
      if (activeItem) {
        closeDropdown(activeItem);
      }
    }, CLOSE_DELAY);
  }

  dropdownItems.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    setupChevron(item);

    const trigger = getTrigger(item);

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('mouseenter', () => openDropdown(item), { signal });
      trigger.addEventListener('pointerenter', () => openDropdown(item), { signal });
      trigger.addEventListener('focusin', () => openDropdown(item), { signal });
    }

    item.addEventListener('mouseenter', () => openDropdown(item), { signal });
    item.addEventListener('pointerenter', () => openDropdown(item), { signal });

    item.addEventListener(
      'mouseleave',
      () => {
        scheduleClose();
      },
      { signal }
    );

    getSubmenus(item).forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;

      submenu.addEventListener(
        'mouseenter',
        () => {
          clearTimeout(closeTimer);
        },
        { signal }
      );

      submenu.addEventListener(
        'pointerenter',
        () => {
          clearTimeout(closeTimer);
        },
        { signal }
      );

      submenu.addEventListener(
        'mouseleave',
        () => {
          scheduleClose();
        },
        { signal }
      );
    });
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && activeItem) {
        closeDropdown(activeItem);
      }
    },
    { signal }
  );
});